/**
 * Fixed-timestep accumulator loop.
 *
 * The simulation advances at a constant 60 Hz regardless of the render rate.
 * A render callback receives the interpolation alpha (0..1) — the fractional
 * progress between the last and next sim ticks — so the renderer can blend
 * object transforms and avoid stutter on high-refresh displays.
 *
 * The accumulator is clamped to a small maximum to avoid the "spiral of
 * death" (sim falling behind real time after a stall).
 */

export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;
/** Hard cap on accumulated time to avoid spiral-of-death after a stall. */
const MAX_ACCUMULATOR = SIM_DT * 10;

/**
 * Start the fixed-step loop.
 *
 * @param sim   advances simulation by one fixed `SIM_DT` step.
 * @param render draws the current sim state; `alpha` is the interpolation
 *               fraction between the previous and current sim tick (0..1).
 */
export function startLoop(sim: (dt: number) => void, render: (alpha: number) => void): () => void {
  let accumulator = 0;
  let lastTime: number | null = null; // seconds; null until the first rAF
  let running = true;

  // rAF delivers a high-resolution timestamp in ms; we work in seconds.
  const tick = (nowMs: number): void => {
    if (!running) return;

    const now = nowMs / 1000;
    if (lastTime === null) lastTime = now; // first frame: zero delta
    let frameTime = now - lastTime;
    lastTime = now;

    // Clamp pathological deltas (tab was backgrounded, debugger pause).
    if (frameTime > MAX_ACCUMULATOR) frameTime = MAX_ACCUMULATOR;

    accumulator += frameTime;

    while (accumulator >= SIM_DT) {
      sim(SIM_DT);
      accumulator -= SIM_DT;
    }

    render(accumulator / SIM_DT);

    requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);

  // Return a stop handle so callers can tear the loop down cleanly.
  return () => {
    running = false;
  };
}
